import { frameId } from "../assets/manifest.ts";
import { frameSizeOf } from "../compile/behaviour.ts";
import { ownFootprintIssues } from "../compile/prop-footprint.ts";
import type { CompileContext } from "../compile/context.ts";
import type { Command } from "../document/commands.ts";
import type { MapDocument, TileRef, WorldDocument } from "../document/types.ts";
import type { TerrainFaceIssue } from "../terrain/faces.ts";
import type { TerrainFace } from "../terrain/types.ts";
import type { DaedalusGameAdapter, ValidationIssue } from "./adapter.ts";
import { behaviourIssues } from "./behaviour.ts";
import { frameDisplayName, type EditorCatalogue } from "./catalogue.ts";
import { contextFrameSize, lowerCellSources, type LoweredCells } from "./cell-sources.ts";
import { issueEntries, issueReport, propBox, type IssueBox, type IssueEntry, type IssueNames, type IssueReport, type MarkerPlace, type PropPlace } from "./issue-entries.ts";
import { ignoreIssuesCommand, shownIssues, stopIgnoringCommand, type IssueIgnoreOutcome } from "./issue-ignores.ts";
import type { LookupList } from "./lookups.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { clearBehaviourTypeCommand } from "./panel-commands.ts";
import { linkEnds, referenceIssues, thingPlace, type LinkEnds, type ReferenceLink, type ReferenceThing } from "./references.ts";
import type { EntityTypeDefinition } from "./schema.ts";
import { thingGroups, type ThingGroup } from "./things.ts";
import { composeValidation } from "./validate-map.ts";

/**
 * The checks over the map in view (ADR-053, ADR-070's amendments): Daedalus's own checks and the game's
 * validation, the map lowered to cells that they read, the Issues list's lines and the map's ignores, the
 * Behaviours view, the names and boxes those name things by, and the revision of what the checks read besides
 * the document. The lowered map and the frame names are memos; nothing here is saved or sent.
 */

/**
 * What the checks need from the store.
 */
export interface ChecksHost {
  readonly document: () => WorldDocument;
  readonly map: () => MapDocument;
  readonly context: CompileContext;
  readonly adapter: DaedalusGameAdapter;
  readonly catalogue: EditorCatalogue;
  readonly objectTypes: () => readonly ObjectTypeDefinition[];
  readonly entityTypes: () => readonly EntityTypeDefinition[];
  readonly faceOf: (terrainId: string) => TerrainFace | undefined;
  readonly malformedFaces: () => readonly TerrainFaceIssue[];
  readonly links: () => readonly ReferenceLink[];
  readonly lookup: (name: string) => LookupList | null;
  readonly collision: () => Uint8Array;
  readonly run: (command: Command) => boolean;
  readonly notify: () => void;
}

/**
 * What callers reach of the checks, through `EditorStore.issues`.
 */
export type IssueSlice = Pick<IssueChecks, "validate" | "loweredCells" | "entries" | "report" | "ignore" | "stopIgnoring" | "stopIgnoringAll" | "thingGroups" | "clearBehaviourType" | "behaviourIssues" | "frameName" | "propBox" | "thingPlace" | "linkEnds" | "changed" | "revision">;

/**
 * The checks.
 */
export class IssueChecks {
  private loweredFrom: MapDocument | null = null;
  private lowered: LoweredCells | null = null;
  private frameNames: { readonly of: EditorCatalogue; readonly names: ReadonlyMap<string, string> } | null = null;
  private changes = 0;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: ChecksHost) {}

  /**
   * Runs Daedalus's own checks and the game's validation over the current map.
   *
   * @returns The behaviour issues (ADR-053), the broken references, the face heights, the zone types, the door
   *   fields, the links of the map's doors, host and arrival points (ADR-122 decision 2.5), the followed zones and
   *   the lookups Daedalus checks, a warning for each prop off the collision cells
   *   (`cellAlignmentIssues`), a warning for each prop whose own footprint holds cells outside its frame
   *   (`ownFootprintIssues`), then what the adapter's `validate` hook reports of the map with its followed zones
   *   lowered to cells, then a warning for each pulled frame its pack no longer holds (`lostPullIssues`) and for
   *   each draft of a studio pack the map places (`draftPlacementIssues`), then a warning for each studio edit whose
   *   frame its sorted pack no longer has (`lostEditIssues`).
   */
  public validate(): readonly ValidationIssue[] {
    return composeValidation({
      world: this.host.document(),
      map: this.host.map(),
      tileSize: this.host.document().tileSize,
      context: this.host.context,
      adapter: this.host.adapter,
      faceOf: this.host.faceOf,
      malformedFaces: this.host.malformedFaces(),
      lowered: this.loweredCells(),
      links: this.host.links(),
      lookup: this.host.lookup,
      collision: this.host.collision,
      lostPulls: this.host.catalogue.lostPulls,
      catalogue: this.host.catalogue,
    });
  }

  /**
   * The map in view with every cells field that follows a zone lowered to the cells it resolves to (ADR-021's
   * amendment of 2026-10-02), what the compile and the game's checks read; computed once for each map document.
   *
   * @returns The lowered map and what each followed zone resolved to.
   */
  public loweredCells(): LoweredCells {
    const map = this.host.map();
    if (this.lowered === null || this.loweredFrom !== map) {
      this.lowered = lowerCellSources(map, this.host.document().tileSize, contextFrameSize(this.host.context));
      this.loweredFrom = map;
    }
    return this.lowered;
  }

  /**
   * The Issues list's lines for this map (P1.6-T29): the issues named by the catalogue's frame names, the
   * compile context's frame sizes and the registered marker types' labels.
   *
   * @param issues The issues, as `validate` returns them.
   * @returns The lines, refusals first.
   */
  public entries(issues: readonly ValidationIssue[]): readonly IssueEntry[] {
    return issueEntries(this.host.map(), issues, this.names());
  }

  /**
   * This map's lines apart (ADR-070's amendment of 2026-10-05): the ones the Issues list shows, the ones a
   * record of the map hides, and how many records hide none.
   *
   * @param issues The issues, as `validate` returns them.
   * @returns The report.
   */
  public report(issues: readonly ValidationIssue[]): IssueReport {
    return issueReport(this.host.map(), issues, this.names());
  }

  /**
   * Ignores an Issues line, or every issue of its kind on this map, as one command that one undo takes back
   * and that also drops the map's stale ignores. The record belongs to the map and to no layer, so no lock or
   * eye refuses it. Nothing of who ignores it or when is recorded here: that is the build server's to say.
   *
   * @param entry The line.
   * @param scope `one` for the line's own record, `kind` for the map's record of its kind.
   * @returns `ignored`; `refused` for a line that may not be ignored; `limit` when the map already holds as
   *   many ignores as it may.
   */
  public ignore(entry: Pick<IssueEntry, "ignoreKey" | "kindKey">, scope: "one" | "kind"): IssueIgnoreOutcome {
    const key = scope === "kind" ? entry.kindKey : entry.ignoreKey;
    if (key === null) {
      return "refused";
    }
    const command = ignoreIssuesCommand(this.host.map(), [key]);
    if (command === "limit") {
      return "limit";
    }
    if (command !== null) {
      this.host.run(command);
    }
    return "ignored";
  }

  /**
   * Stops ignoring by the records' keys, as one command.
   *
   * @param keys The keys (`IssueEntry.ignored.key`).
   * @returns False when the map holds none of them.
   */
  public stopIgnoring(keys: readonly string[]): boolean {
    const command = stopIgnoringCommand(this.host.map(), keys);
    return command !== null && this.host.run(command);
  }

  /**
   * Stops ignoring every issue of this map, as one command.
   *
   * @returns How many records it removed, 0 when the map held none.
   */
  public stopIgnoringAll(): number {
    const map = this.host.map();
    const held = Object.keys(map.ignoredIssues ?? {}).length;
    const command = stopIgnoringCommand(map, null);
    return command !== null && this.host.run(command) ? held : 0;
  }

  /**
   * The Behaviours view's groups for this map (P1.6-T30): every registered type with the props carrying it,
   * then the types the map uses that the game does not register, each prop named as the Issues list names it.
   *
   * @param issues The Issues list's lines, as the live check last gave them.
   * @returns The groups.
   */
  public thingGroups(issues: readonly IssueEntry[]): readonly ThingGroup[] {
    return thingGroups(this.host.map(), this.host.objectTypes(), issues, this.names());
  }

  /**
   * Takes a behaviour type off every prop of the map carrying it, as one command that one undo takes back
   * (P1.6-T30's Clear, for a type the game no longer registers).
   *
   * @param type The behaviour type's id.
   * @returns False when no prop carries the type, or a lock or a hidden layer refused the command; nothing is
   *   recorded then.
   */
  public clearBehaviourType(type: string): boolean {
    const command = clearBehaviourTypeCommand(this.host.map(), type);
    return command !== null && this.host.run(command);
  }

  /**
   * What the compiler would refuse about one prop's behaviour, and what it would accept but warn about
   * (P1.1-T6), then its broken references (P1.6-T35) and its own footprint's cells outside its frame, for the inspector to show beside it. A warning the map's
   * record ignores is left out, as the Issues list leaves it out (ADR-070's amendment of 2026-10-05).
   *
   * @param layerId Prop layer id.
   * @param propId Prop id.
   * @returns The issues, empty when the prop carries no behaviour, the behaviour is complete or every issue
   *   is ignored.
   */
  public behaviourIssues(layerId: string, propId: string): readonly ValidationIssue[] {
    const map = this.host.map();
    const own = [...behaviourIssues(map, this.host.objectTypes(), this.host.context, this.host.document().tileSize), ...this.referenceIssues(), ...ownFootprintIssues(map, this.host.context, this.host.document().tileSize)].filter((issue) => issue.layerId === layerId && issue.propId === propId);
    return shownIssues(map, own);
  }

  /**
   * A frame's name: the catalogue's, or its tile number where the catalogue has none, as the Issues list and
   * the command palette name a placed prop.
   *
   * @param frame The frame.
   * @returns The name.
   */
  public frameName(frame: TileRef): string {
    return this.frameNameIndex().get(frameId(frame.tileset, null, frame.tile)) ?? frameDisplayName(this.host.catalogue, frame);
  }

  /**
   * The cells a placed prop's art covers on this map, in world pixels, the box Go to and the command palette
   * frame (`propBox`).
   *
   * @param layerId The prop's layer.
   * @param propId The prop.
   * @returns The box, or null when the map holds no such prop.
   */
  public propBox(layerId: string, propId: string): IssueBox | null {
    const layer = this.host.map().layers.find((l) => l.id === layerId);
    const prop = layer?.kind === "prop" ? layer.props.find((p) => p.id === propId) : undefined;
    return prop === undefined ? null : propBox(prop, (frame) => frameSizeOf(this.host.context, frameId(frame.tileset, null, frame.tile)), this.host.document().tileSize);
  }

  /**
   * Where a prop or a marker stands, named as the Issues list names it, for a reference's slot and Referenced by
   * (P1.6-T35).
   *
   * @param thing The thing.
   * @returns Its place, or null when the map no longer holds it.
   */
  public thingPlace(thing: ReferenceThing): PropPlace | MarkerPlace | null {
    return thingPlace(this.host.map(), thing, this.names());
  }

  /**
   * The ends of every sound reference link on the map, in world pixels (P1.6-T35).
   *
   * @returns The ends.
   */
  public linkEnds(): readonly LinkEnds[] {
    return linkEnds(this.host.map(), this.host.links(), this.names());
  }

  /**
   * Tells the store that what its checks report has changed with neither the document nor the compile context
   * changing: a game's `validate` may read state of its own, such as a note it carries until the world is saved.
   * The live issue check then runs again; nothing is edited and nothing can be undone.
   */
  public changed(): void {
    this.bump();
    this.host.notify();
  }

  /**
   * Counts one change to what the checks read besides the document, telling no one: the caller notifies.
   */
  public bump(): void {
    this.changes += 1;
  }

  /**
   * Counts the changes made to what the checks read besides the document (a footprint set from the inspector, a
   * game's own state through `checksChanged`), so the live issue check follows them too.
   *
   * @returns The revision.
   */
  public get revision(): number {
    return this.changes;
  }

  /**
   * The refusals broken references raise.
   *
   * @returns The issues, in document order.
   */
  private referenceIssues(): readonly ValidationIssue[] {
    return referenceIssues(this.host.map(), { objectTypes: this.host.objectTypes(), entityTypes: this.host.entityTypes() }, this.host.links());
  }

  /**
   * What the Issues list and the Behaviours view name things by.
   *
   * @returns The lookups.
   */
  private names(): IssueNames {
    const document = this.host.document();
    return {
      tileSize: document.tileSize,
      frameName: (frame) => this.frameName(frame),
      frameSize: (frame) => frameSizeOf(this.host.context, frameId(frame.tileset, null, frame.tile)),
      entityTypes: this.host.entityTypes(),
      tilesets: document.tilesets.map((t) => t.id),
    };
  }

  /**
   * The catalogue's frame names by frame id, built once for each catalogue the store reads through (a growth
   * replaces the catalogue, ADR-130 decision 2.8).
   *
   * @returns The names.
   */
  private frameNameIndex(): ReadonlyMap<string, string> {
    const catalogue = this.host.catalogue;
    if (this.frameNames?.of !== catalogue) {
      this.frameNames = { of: catalogue, names: new Map(catalogue.props.map((frame) => [frameId(frame.tileset, null, frame.tile), frame.name])) };
    }
    return this.frameNames.names;
  }
}
